<template>
  <div class="my-tasks-page">
    <div class="container mx-auto p-4">
      <div class="flex items-center justify-between mb-4">
        <h1 class="text-2xl font-bold text-white shrink-0">My Tasks</h1>
        <div class="flex flex-wrap gap-2 justify-center items-center">
          <!-- <UButton
            v-if="hasPendingTasks"
            icon="material-symbols-light:sync-rounded"
            size="md"
            variant="soft"
            color="primary"
            :loading="isRefreshingAll"
            @click="refreshAllPendingTasks()"
          >
            {{ isRefreshingAll ? "Refreshing..." : "Refresh All" }}
          </UButton> -->
          <UButton
            icon="i-lucide-trash-2"
            size="sm"
            color="neutral"
            variant="soft"
            v-if="token"
            @click="deletedTasksOpen = true"
            title="Trash"
          >
            <!-- Deleted -->
          </UButton>
          <span v-if="lastUpdateTime && token" class="text-xs text-gray-500">
            {{ lastUpdateTime }}
          </span>
          <UButton
            icon="material-symbols-light:directory-sync-rounded"
            size="md"
            color="neutral"
            variant="ghost"
            :loading="loading"
            @click="loadTasks(true)"
            v-if="token"
          >
            <!-- Refresh All -->
          </UButton>
        </div>
      </div>

      <div
        v-if="isAdmin"
        class="mb-4 rounded-xl border border-gray-800 bg-[#111827]/70 p-4"
      >
        <div
          class="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between"
        >
          <div class="flex flex-col justify-center">
            <div class="text-sm font-semibold text-white">
              Admin User Task Filter
            </div>
            <div class="mt-1 text-xs text-gray-400">
              Search by email, task ID, or user ID to locate a user and view
              that user's tasks and credits. Leave it empty to view all tasks.
            </div>
          </div>

          <div class="flex w-full flex-col gap-2 xl:w-auto justify-center">
            <UTabs
              v-model="adminQueryType"
              :items="adminQueryTypeItems"
              value-key="value"
              size="sm"
              color="primary"
              variant="pill"
              :content="false"
              class="w-full"
              :ui="{
                trigger:
                  'group inline-flex items-center gap-1.5 transition-colors data-[state=active]:text-white',
              }"
            />

            <div class="flex w-full flex-col gap-2 sm:flex-row items-center">
              <UInput
                v-model="adminQueryInput"
                size="sm"
                :icon="adminQueryInputIcon"
                :placeholder="adminQueryInputPlaceholder"
                class="w-full sm:w-80"
                @keyup.enter="applyAdminTaskFilter"
              />
              <UButton
                size="sm"
                color="primary"
                variant="solid"
                @click="applyAdminTaskFilter"
                class="text-white"
              >
                Filter
              </UButton>
              <UButton
                v-if="adminQueryInput || activeAdminQueryValue"
                size="sm"
                color="neutral"
                variant="soft"
                @click="clearAdminTaskFilter"
                class="text-white"
              >
                Clear
              </UButton>
            </div>
          </div>
        </div>

        <div
          v-if="activeAdminQueryValue"
          class="mt-3 rounded-lg border border-gray-800 bg-black/20 p-3"
        >
          <div v-if="adminTargetUserSummary" class="space-y-2">
            <div class="flex flex-wrap gap-x-4 gap-y-1 text-sm text-white">
              <div>
                User ID:
                <span class="ml-1 font-semibold">
                  {{ adminTargetUserSummary.user_id }}
                </span>
              </div>
              <div>
                Email:
                <span class="ml-1">{{
                  adminTargetUserSummary.email_address
                }}</span>
              </div>
            </div>
            <div class="text-sm text-white">
              Search Type:
              <span class="ml-1 font-semibold">
                {{ activeAdminQueryTypeLabel }}
              </span>
              <span class="ml-2 text-xs text-gray-400">
                {{ activeAdminQueryValue }}
              </span>
            </div>
            <div class="text-sm text-white">
              Plan:
              <span class="ml-1">
                {{ adminTargetUserSummary.product_name || "Free" }}
              </span>
            </div>
            <div class="flex flex-wrap gap-4 text-sm text-gray-300">
              <div>
                Subscription Plan Credits:
                <span class="ml-1 font-semibold text-white">
                  {{ formatCreditValue(adminTargetUserSummary.plan_credits) }}
                </span>
              </div>
              <div>
                Remaining Credits:
                <span class="ml-1 font-semibold text-white">
                  {{ formatCreditValue(adminTargetUserSummary.credits_sum) }}
                </span>
              </div>
            </div>
          </div>
          <div
            v-else-if="adminTargetUserNotFound"
            class="text-sm text-amber-300"
          >
            No user found for {{ activeAdminQueryValue }}.
          </div>
        </div>
      </div>

      <div
        v-if="token"
        class="w-full mb-4 flex flex-nowrap items-center gap-2 overflow-x-auto custom-scroll"
      >
        <USelectMenu
          v-if="showGroupTabs"
          v-model="groupTab"
          :items="groupTabItems"
          value-key="value"
          size="md"
          color="primary"
          variant="soft"
          :icon="selectedGroupTabItem?.icon"
          class="min-w-[168px] shrink-0"
          :ui="{
            base: 'rounded-full',
            content: 'min-w-fit',
            trailingIcon:
              'group-data-[state=open]:rotate-180 transition-transform duration-200',
          }"
        />

        <USelectMenu
          v-if="showToolTabs"
          v-model="toolTab"
          :items="toolTabItems"
          value-key="value"
          size="md"
          color="primary"
          variant="soft"
          :icon="selectedToolTabItem?.icon"
          class="min-w-[168px] shrink-0"
          :ui="{
            base: 'rounded-full',
            content: 'min-w-fit',
            trailingIcon:
              'group-data-[state=open]:rotate-180 transition-transform duration-200',
          }"
        />

        <USelectMenu
          v-if="showModelTitleTabs && toolTab !== 'all'"
          v-model="modelTitleTab"
          :items="modelTitleTabItems"
          value-key="value"
          size="md"
          color="primary"
          variant="soft"
          :icon="
            selectedModelTitleTabItem?.avatar
              ? undefined
              : selectedModelTitleTabItem?.icon
          "
          :avatar="selectedModelTitleTabItem?.avatar"
          class="min-w-[168px] shrink-0"
          :ui="{
            base: 'rounded-full',
            content: 'min-w-fit',
            leadingAvatar: 'rounded-none bg-transparent',
            itemLeadingAvatar: 'rounded-none bg-transparent',
            trailingIcon:
              'group-data-[state=open]:rotate-180 transition-transform duration-200',
          }"
        />

        <USelectMenu
          v-if="
            showModelVersionTabs && toolTab !== 'all' && modelTitleTab !== 'all'
          "
          v-model="modelVersionTab"
          :items="modelVersionTabItems"
          value-key="value"
          size="md"
          color="primary"
          variant="soft"
          :icon="
            selectedModelVersionTabItem?.avatar
              ? undefined
              : selectedModelVersionTabItem?.icon
          "
          :avatar="selectedModelVersionTabItem?.avatar"
          class="min-w-[168px] shrink-0"
          :ui="{
            base: 'rounded-full',
            content: 'min-w-fit',
            leadingAvatar: 'rounded-none bg-transparent',
            itemLeadingAvatar: 'rounded-none bg-transparent',
            trailingIcon:
              'group-data-[state=open]:rotate-180 transition-transform duration-200',
          }"
        />

        <UInput
          v-model="taskSearchInput"
          size="md"
          color="primary"
          variant="soft"
          placeholder="Search by task ID, prompt, or filter values"
          class="min-w-[340px] shrink-0"
        >
          <template #leading>
            <UIcon name="i-lucide-search" />
          </template>
          <template #trailing>
            <UButton
              v-if="taskSearchInput"
              icon="i-lucide-x"
              color="neutral"
              variant="ghost"
              size="xs"
              aria-label="Clear search"
              @click="clearTaskSearch"
            />
          </template>
        </UInput>

        <UButton
          size="sm"
          color="primary"
          variant="solid"
          class="text-white"
          @click="applyTaskSearch"
        >
          Filter
        </UButton>
      </div>

      <!-- 网络加载状态 -->
      <div
        v-if="loading && tasks.length === 0"
        class="flex justify-center items-center py-20"
      >
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"
        ></div>
      </div>

      <!-- 筛选状态 -->
      <div
        v-else-if="isFiltering"
        class="flex justify-center items-center py-20"
      >
        <div class="flex flex-col items-center gap-3">
          <div
            class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"
          ></div>
        </div>
      </div>

      <!-- 无任务状态 -->
      <div
        v-else-if="
          !isFiltering && (!filteredTasks || filteredTasks.length === 0)
        "
        class="text-center py-20"
      >
        <UEmpty
          icon="i-lucide-file"
          title="No taks found"
          description="It looks like you haven't added any tasks. Create one to get started."
          :ui="{
            root: 'ring-0',
            label: 'text-white',
          }"
          :actions="[
            {
              icon: 'i-lucide-plus',
              label: 'Create new',
              to: '/ai/image/text-to-image/',
              ui: {
                base: 'text-white',
              },
            },
            {
              icon: 'i-lucide-refresh-cw',
              label: 'Refresh',
              color: 'neutral',
              variant: 'subtle',
              onClick: () => {
                // 刷新任务
                loadTasks();
              },
            },
          ]"
        />
      </div>

      <!-- 任务列表 -->
      <div
        v-else
        class="cards-scroll overflow-auto px-1"
        ref="cardsContainerRef"
      >
        <Transition name="fade" mode="out-in">
          <div
            class="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-6"
          >
            <TransitionGroup name="task-item" tag="div" class="contents">
              <UCard
                v-for="task in pagedTasks"
                :key="`task-card-${task.id}`"
                variant="outline"
                class="border-gray-700 w-full flex flex-col justify-between"
                :ui="{
                  header: '!px-3 !py-2 !border-0 bg-[#1a2332]',
                  body: '!px-0 !py-0 !border-0 flex-1 flex flex-col justify-end',
                  footer: '!px-3 !py-2 !border-0 bg-[#1a2332]',
                }"
              >
                <!-- Header -->
                <template #header>
                  <!-- 任务信息 -->
                  <div class="flex items-start justify-between overflow-hidden">
                    <div class="min-w-0 flex-1">
                      <div class="flex flex-col gap-1">
                        <!-- 第一行: 状态、工具名称 -->
                        <div class="flex items-center gap-2 flex-wrap">
                          <!-- 状态 -->
                          <div
                            class="w-2 h-2 rounded-full bg-green-500 shrink-0"
                          ></div>
                          <!-- 工具名称 高亮主显示（可点击跳转） -->
                          <NuxtLink
                            v-if="getTaskPageUrl(task)"
                            :to="getTaskPageUrl(task)"
                            class="text-green-400 text-base font-semibold hover:text-green-300 hover:underline transition-colors"
                          >
                            {{ toolTitle(task.toolName, task).label }}
                          </NuxtLink>
                          <span
                            v-else
                            class="text-green-400 text-base font-semibold"
                          >
                            {{ toolTitle(task.toolName, task).label }}
                          </span>
                        </div>
                        <!-- 时间 暗色调 -->
                        <span class="text-xs text-gray-500">
                          {{ formatTime(task.createdAt) }}
                        </span>
                        <!-- 第二行: 其他信息，暗色调为主 -->
                        <div class="flex items-center gap-2 flex-wrap mt-0.5">
                          <!-- 模型 -->
                          <span
                            v-if="selectedModel(task)?.avatar.src"
                            class="text-xs text-gray-400 mr-2 flex items-center"
                          >
                            <img
                              v-if="selectedModel(task)?.avatar"
                              :src="selectedModel(task)?.avatar.src"
                              alt="model icon"
                              class="inline-block align-middle w-4 h-4 mr-1"
                              style="opacity: 0.5"
                            />
                            {{ taskModelTitle(task) }}
                            <!-- {{ selectedModel(task)?.label }}  -->
                          </span>
                          <!-- 比例 -->
                          <span
                            v-if="task.ratio"
                            class="text-xs text-gray-400 mr-2 flex items-center"
                          >
                            {{ selectedModelRatio(task) }}
                          </span>
                          <!-- 是否公开 -->
                          <span
                            v-if="task.isPublic"
                            class="text-xs text-gray-400 flex items-center mr-2"
                          >
                            <UIcon
                              name="i-lucide-eye"
                              class="mr-1"
                              style="opacity: 0.5"
                            />
                            Public
                          </span>
                          <!-- 是否归档 -->
                          <span
                            v-if="task.isArchived"
                            class="text-xs text-gray-400 flex items-center mr-2"
                          >
                            <UIcon
                              name="majesticons:archive-line"
                              class="mr-1"
                              style="opacity: 0.5"
                            />
                          </span>
                          <!-- 积分 -->
                          <span
                            v-if="task.creditCost > 0"
                            class="text-xs text-gray-400 flex items-center mr-2"
                          >
                            <UIcon
                              name="material-symbols-light:diamond-shine-outline-rounded"
                              class="mr-1"
                              style="opacity: 0.5"
                            />
                            {{ task.creditCost }}
                          </span>
                          <pre
                            v-show="isDev"
                            class="text-xs text-white whitespace-pre-wrap wrap-break-word"
                            v-html="formatJSONWithLinks(task)"
                          ></pre>
                        </div>
                      </div>
                      <div
                        class="mt-1 flex items-center gap-1 text-sm text-muted min-w-0"
                      >
                        <UPopover
                          mode="hover"
                          arrow
                          :open-delay="150"
                          :close-delay="150"
                          v-if="task?.inPutImageUrls?.length > 0"
                          :open="
                            isDev ? ($event) => (isDev = $event) : undefined
                          "
                        >
                          <div
                            class="cursor-pointer hover:opacity-80 transition-opacity"
                          >
                            <div
                              class="relative w-6 h-6 rounded overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm bg-gray-100 dark:bg-gray-800"
                            >
                              <ViewportImage
                                :src="task.inPutImageUrls[0]"
                                alt="preview thumbnail"
                                img-class="w-full h-full object-cover"
                                :root-margin="'200px'"
                              >
                                <template #loading>
                                  <UIcon
                                    name="i-lucide-loader-2"
                                    class="w-3 h-3 animate-spin text-gray-400"
                                  />
                                </template>
                              </ViewportImage>
                            </div>
                          </div>

                          <template #content>
                            <div class="p-2">
                              <div
                                class="flex flex-col gap-4 overflow-y-auto max-h-80 min-h-20 w-64 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 items-center custom-scroll"
                              >
                                <div
                                  v-for="(url, index) in task?.inPutImageUrls"
                                  :key="`input-image-${task.id}-${index}`"
                                  class="relative"
                                >
                                  <img
                                    :ref="
                                      (el) => {
                                        if (el) {
                                          // 检查是否已有缓存的 blob URL
                                          if (imageBlobCache.has(url)) {
                                            el.src = imageBlobCache.get(url);
                                            imageLoadedStates[url] = true;
                                          } else {
                                            // 检查当前元素是否已加载
                                            nextTick(() => {
                                              checkImageCache(url, el);
                                            });
                                          }
                                        }
                                      }
                                    "
                                    :src="
                                      imageBlobCache.has(url)
                                        ? imageBlobCache.get(url)
                                        : url
                                    "
                                    :alt="`preview ${index + 1}`"
                                    class="w-auto h-auto rounded-md shadow-md object-contain cursor-zoom-in"
                                    :class="{
                                      'opacity-0': !imageLoadedStates[url],
                                    }"
                                    @load="handleImageLoad(url, $event)"
                                    @error="handleImageLoad(url, $event)"
                                    @click="openImagePreview(url)"
                                  />
                                  <div
                                    v-show="!imageLoadedStates[url]"
                                    class="absolute inset-0 flex justify-center items-center pointer-events-none"
                                  >
                                    <div
                                      class="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"
                                    ></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </template>
                        </UPopover>

                        <div
                          v-for="videoFile in task?.videoFiles"
                          :key="videoFile"
                        >
                          <UPopover
                            mode="hover"
                            arrow
                            :open-delay="150"
                            :close-delay="150"
                            v-if="videoFile"
                            :open="
                              isDev ? ($event) => (isDev = $event) : undefined
                            "
                          >
                            <div
                              class="cursor-pointer hover:opacity-80 transition-opacity relative"
                            >
                              <div
                                class="relative w-6 h-6 rounded overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm bg-gray-100 dark:bg-gray-800 flex items-center justify-center"
                              >
                                <ViewportVideo
                                  :src="videoFile"
                                  video-class="absolute inset-0 w-full h-full object-cover z-10"
                                  :muted="true"
                                  :playsinline="true"
                                  :preload="'metadata'"
                                  :disable-picture-in-picture="true"
                                  :key="`video-thumbnail-${task.id}`"
                                  :root-margin="'200px'"
                                >
                                  <template #placeholder>
                                    <UIcon
                                      name="i-lucide-loader-2"
                                      class="w-3 h-3 animate-spin text-gray-400"
                                    />
                                  </template>
                                </ViewportVideo>
                                <div
                                  class="absolute inset-0 items-center justify-center bg-black/10 z-20 pointer-events-none flex"
                                >
                                  <UIcon
                                    name="i-lucide-play"
                                    class="w-2.5 h-2.5 text-white drop-shadow-sm"
                                  />
                                </div>
                              </div>
                            </div>

                            <template #content>
                              <div class="p-2">
                                <div
                                  class="flex flex-col gap-2 overflow-y-auto h-80 w-64 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100"
                                >
                                  <ViewportVideo
                                    :src="videoFile"
                                    video-class="object-contain w-full h-full cursor-zoom-in"
                                    :autoplay="false"
                                    :muted="true"
                                    :playsinline="true"
                                    :controls="true"
                                    :controls-list="inlineVideoControlsList"
                                    :disable-picture-in-picture="true"
                                    :key="`video-preview-${videoFile}`"
                                    @loadstart="isVideoLoading = true"
                                    @canplay="isVideoLoading = false"
                                    @error="isVideoLoading = false"
                                    @click="openImagePreview(videoFile)"
                                  />
                                </div>
                              </div>
                            </template>
                          </UPopover>
                        </div>

                        <span
                          class="truncate"
                          v-if="task.prompt"
                          :title="task.prompt"
                          >{{ task.prompt }}</span
                        >
                        <UButton
                          v-if="task.prompt"
                          variant="link"
                          color="neutral"
                          size="xs"
                          icon="i-lucide-copy"
                          aria-label="Copy prompt"
                          class="shrink-0"
                          @click="
                            copyPrompt(
                              task.lyrics
                                ? `Prompt:\n${task.prompt}\n\nLyrics:\n${task.lyrics}`
                                : task.prompt
                            )
                          "
                        />
                      </div>
                    </div>

                    <div class="flex items-center gap-1 shrink-0">
                      <!-- 刷新按钮（仅在预测中状态显示） -->
                      <UButton
                        v-if="isTaskPending(task)"
                        icon="material-symbols-light:sync-rounded"
                        color="primary"
                        variant="ghost"
                        :loading="
                          refreshingTaskId === task.id || isRefreshingAll
                        "
                        aria-label="Refresh"
                        title="Refresh task status"
                        @click.stop="refreshSingleTask(task.id)"
                      />
                      <UPopover
                        v-else
                        v-model:open="deletePopoverOpen[task.id]"
                      >
                        <UButton
                          icon="material-symbols-light:cancel-outline-rounded"
                          color="neutral"
                          variant="ghost"
                          :disabled="isTaskPending(task)"
                          aria-label="Delete"
                          title="Delete"
                          @click.stop
                        />

                        <template #content>
                          <div
                            class="p-4 space-y-3 min-w-48 sm:min-w-64 max-w-[calc(100vw-2rem)]"
                          >
                            <div class="flex items-start gap-3">
                              <div class="shrink-0 mt-0.5">
                                <UIcon
                                  name="i-lucide-alert-circle"
                                  class="text-warning w-5 h-5"
                                />
                              </div>
                              <div>
                                <h3 class="font-semibold text-sm mb-1">
                                  Delete Task
                                </h3>
                                <p class="text-sm text-muted">
                                  Are you sure you want to mark this task as
                                  deleted? The data will be completely deleted
                                  after 7 days.
                                </p>
                              </div>
                            </div>
                            <div class="flex items-center justify-end gap-2">
                              <UButton
                                color="neutral"
                                variant="ghost"
                                size="sm"
                                :disabled="deletingTaskId === task.id"
                                @click.stop="deletePopoverOpen[task.id] = false"
                              >
                                Cancel
                              </UButton>
                              <UButton
                                color="error"
                                variant="solid"
                                size="sm"
                                :loading="deletingTaskId === task.id"
                                :disabled="deletingTaskId === task.id"
                                @click.stop="handleDeleteTask(task.id)"
                              >
                                Delete
                              </UButton>
                            </div>
                          </div>
                        </template>
                      </UPopover>
                    </div>
                  </div>
                </template>

                <!-- Body -->
                <div class="flex-1 flex flex-col justify-end">
                  <!-- Image -->
                  <div
                    class="relative w-full aspect-video overflow-hidden"
                    :class="imgRatio"
                  >
                    <!-- 预测中状态的加载动画 -->
                    <div
                      v-if="isTaskPending(task)"
                      class="w-full h-full flex flex-col items-center justify-center absolute inset-0"
                    >
                      <div class="flex justify-center items-center py-2">
                        <div
                          class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"
                        ></div>
                      </div>
                      <p class="mt-3 text-center text-sm text-gray-400">
                        {{
                          task.status === "starting"
                            ? "Processing…"
                            : "Processing…"
                        }}
                      </p>
                    </div>
                    <!-- 已有图片 - 使用懒加载 -->
                    <TaskOutputImages
                      v-else-if="
                        taskConfigData[getTaskConfigKey(task)]?.isShowImage &&
                        getTaskOutputUrls(task).length
                      "
                      :urls="getTaskOutputUrls(task)"
                      :alt="task.prompt"
                      v-model:active-index="taskOutputActiveIndex[task.id]"
                      class="absolute inset-0 w-full h-full"
                    />

                    <!-- 已有视频 - 使用懒加载 -->
                    <ViewportVideo
                      v-else-if="
                        task.outPutImageUrl &&
                        taskConfigData[getTaskConfigKey(task)]?.isShowVideo
                      "
                      :src="task.outPutImageUrl"
                      video-class="object-contain w-full h-full cursor-zoom-in"
                      :autoplay="false"
                      :muted="true"
                      :playsinline="true"
                      :controls="true"
                      :controls-list="inlineVideoControlsList"
                      :disable-picture-in-picture="true"
                      @loadstart="isVideoLoading = true"
                      @canplay="isVideoLoading = false"
                      @error="isVideoLoading = false"
                      @click="openImagePreview(task.outPutImageUrl)"
                    />

                    <!-- 音频 -->
                    <VinylRecordPlayer
                      v-else-if="
                        task.outPutImageUrl &&
                        taskConfigData[getTaskConfigKey(task)]?.isShowRecord
                      "
                      class="w-full h-full"
                      :audio-src="task.outPutImageUrl"
                      :player-size="0"
                    />

                    <!-- 图片对比 - 使用懒加载 -->
                    <ImageComparator
                      v-else-if="
                        task.outPutImageUrl &&
                        taskConfigData[getTaskConfigKey(task)]
                          ?.isShowImageComparator
                      "
                      :left-image="task.inPutImageUrls[0]"
                      :right-image="task.outPutImageUrl"
                    />

                    <!-- 无图片 -->
                    <div
                      v-else
                      class="w-full h-full flex items-center justify-center absolute inset-0"
                    >
                      <p class="text-gray-500">No image available</p>
                    </div>
                  </div>
                </div>

                <!-- Footer -->
                <template #footer>
                  <div class="h-12 flex items-center justify-between">
                    <!-- More Menu -->
                    <UDropdownMenu :items="getDropdownItems(task)">
                      <UButton
                        class="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
                        icon="nrk:more"
                        color="neutral"
                        variant="ghost"
                        title="More"
                        >More
                      </UButton>
                    </UDropdownMenu>

                    <div class="flex items-center gap-2">
                      <!-- Download Button -->
                      <UButton
                        @click="downloadImage(task)"
                        class="rounded-lg border border-gray-600 text-gray-300 hover:text-white hover:border-gray-500 transition-colors"
                        title="Download"
                        icon="material-symbols-light:download-rounded"
                        variant="ghost"
                        color="neutral"
                        :disabled="
                          !getTaskOutputUrls(task).length || isTaskPending(task)
                        "
                      >
                      </UButton>

                      <!-- Generate Video Button -->
                      <UButton
                        class="text-white transition-colors flex items-center gap-2"
                        icon="material-symbols-light:star-shine-outline-rounded"
                        variant="solid"
                        color="primary"
                        title="Generate Video"
                        v-if="
                          taskConfigData[getTaskConfigKey(task)]?.isShowImage
                        "
                        @click="generateVideo(task)"
                      >
                        Generate Video
                      </UButton>
                    </div>
                  </div>
                </template>
              </UCard>
            </TransitionGroup>
          </div>
        </Transition>
        <!-- Pagination -->
        <div
          class="mt-6 flex justify-center"
          v-if="
            filteredTasks &&
            filteredTasks.length > 0 &&
            filteredTasks.length / evenItemsPerPage > 1
          "
        >
          <UPagination
            v-model:page="page"
            :total="filteredTasks.length"
            :items-per-page="evenItemsPerPage"
            color="primary"
            variant="subtle"
            :sibling-count="1"
            @update:page="onPageChange"
          />
        </div>
      </div>
    </div>
    <DeletedTasksModal
      v-model:open="deletedTasksOpen"
      :user-id="userId"
      :menu-data="menuData"
      @restored="loadTasks(true)"
    />
  </div>
</template>

<script setup>
import {
  reactive,
  computed,
  ref,
  inject,
  nextTick,
  onMounted,
  watch,
  onUnmounted,
  onDeactivated,
  onActivated,
} from "vue";
import { useStore } from "vuex";
import { formatJSONWithLinks } from "@/common/utils/dev";
import VinylRecordPlayer from "@/components/ai-tools/VinylRecordPlayer.vue";
import TaskOutputImages from "@/components/ai-tools/TaskOutputImages.vue";
import DeletedTasksModal from "@/components/my-tasks/DeletedTasksModal.vue";
const { openImagePreview } = useImagePreview();
const inlineVideoControlsList =
  "nodownload nofullscreen noremoteplayback noplaybackrate";
definePageMeta({ layout: "default-new", ssr: false });
useHead({ title: "My Tasks" });
import { useMediaQuery, useWindowFocus } from "@vueuse/core";
import localforage from "localforage";
import { aiToolConfig } from "@/components/ai-tools/aiToolConfig";
import ViewportImage from "~/components/viewport/ViewportImage.vue";
import ViewportVideo from "~/components/viewport/ViewportVideo.vue";
import ImageComparator from "@/components/ai-tools/ImageComparator.vue";
import {
  useAiR2ModelsAndEffects,
  normalizeEffectsList,
} from "@/composables/useAiR2ModelsAndEffects";
import {
  prepareAdminQuerySubmit,
  useAdminQueryTypeAutoDetect,
} from "@/composables/useAdminQueryInference";
import { normalizeCatalogUrl, toInternalRoute } from "@/utils/aiCatalogData";
import { resolveUserR2Url, stripKnownR2Domain } from "@/utils/r2Url";

const isMobile = useMediaQuery("(max-width: 768px)");
const store = useStore();
const toast = useToast();
const { isAdmin } = useAdmin();
const r2CachedFetch = useR2CachedFetch();

const token = computed(() => store.state.user.token);
const tasks = ref([]);
const loading = ref(false);
const deletedTasksOpen = ref(false);
const imgRatio = ref("aspect-[16/9]");
const groupTab = ref("all");
const toolTab = ref("all");
const modelTitleTab = ref("all");
const modelVersionTab = ref("all");
const taskSearchInput = ref("");
const taskSearchQuery = ref("");
const lastUpdateTime = ref("");
const adminQueryType = ref("email");
const adminQueryInput = ref("");

useAdminQueryTypeAutoDetect(adminQueryInput, adminQueryType);

const activeAdminQueryType = ref("");
const activeAdminQueryValue = ref("");
const adminTargetUserSummary = ref(null);
const adminTargetUserNotFound = ref(false);

// 模型 / effects：与产品页 GenerativePanel 一致（useRequestFetch + useAsyncData，共用缓存 key）
const { modelsByTool, effectsByTool } = useAiR2ModelsAndEffects();
const deletePopoverOpen = ref({});
const deletingTaskId = ref(null);

// 刷新相关状态
const refreshingTaskId = ref(null);
const isRefreshingAll = ref(false);

// 多输出图片：记录每个任务当前选中的输出序号
const taskOutputActiveIndex = reactive({});
// 跟踪图片加载状态，避免重复请求
const imageLoadedStates = reactive({});
// 缓存已加载图片的 blob URL，完全避免重复网络请求
const imageBlobCache = new Map();

// 将图片 URL 转换为 blob URL 并缓存
const getCachedImageUrl = async (url) => {
  // 如果已经有缓存的 blob URL，直接返回
  if (imageBlobCache.has(url)) {
    imageLoadedStates[url] = true;
    return imageBlobCache.get(url);
  }

  try {
    // 获取图片并转换为 blob
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    // 缓存 blob URL
    imageBlobCache.set(url, blobUrl);
    imageLoadedStates[url] = true;

    return blobUrl;
  } catch (error) {
    console.error("Failed to cache image:", error);
    // 如果失败，返回原始 URL
    imageLoadedStates[url] = true;
    return url;
  }
};

// 处理图片加载
const handleImageLoad = (url, event) => {
  imageLoadedStates[url] = true;
  // 如果还没有缓存，尝试缓存
  if (!imageBlobCache.has(url) && event.target?.src) {
    getCachedImageUrl(url).catch(() => {
      // 静默失败，使用原始 URL
    });
  }
};

// 检查图片是否已经在浏览器缓存中
const checkImageCache = (url, imgElement) => {
  if (imgElement && imgElement.complete && imgElement.naturalHeight !== 0) {
    imageLoadedStates[url] = true;
    // 尝试缓存图片
    getCachedImageUrl(url).catch(() => {
      // 静默失败
    });
    return true;
  }
  return false;
};

// 卡片配置相关
const taskConfigData = ref({});

// 从商店获取用户 ID
const userId = computed(() => store.state.user.userInfo.user_info_id || "");
const adminQueryTypeItems = [
  { label: "Email", value: "email", icon: "i-lucide-mail" },
  { label: "Task ID", value: "task_id", icon: "i-lucide-hash" },
  { label: "User ID", value: "user_id", icon: "i-lucide-user-round" },
];
const adminQueryTypeMeta = computed(() =>
  adminQueryTypeItems.reduce((acc, item) => {
    acc[item.value] = item;
    return acc;
  }, {})
);
const activeAdminQueryTypeLabel = computed(
  () =>
    adminQueryTypeMeta.value?.[activeAdminQueryType.value]?.label || "Unknown"
);
const adminQueryInputPlaceholder = computed(() => {
  if (adminQueryType.value === "task_id") return "Search tasks by task ID";
  if (adminQueryType.value === "user_id") return "Search tasks by user ID";
  return "Search tasks by user email";
});
const adminQueryInputIcon = computed(() => {
  return (
    adminQueryTypeMeta.value?.[adminQueryType.value]?.icon || "i-lucide-search"
  );
});
const taskCacheScope = computed(() => {
  if (!userId.value) return "anonymous";
  if (!isAdmin.value) return `user:${userId.value}`;
  return activeAdminQueryValue.value
    ? `admin:${activeAdminQueryType.value}:${activeAdminQueryValue.value}`
    : "admin:all";
});

/** user_tasks 缓存：IndexedDB（localforage），避免大体量数据撑爆 localStorage */
const userTasksStorage = localforage.createInstance({
  name: "sharkfoto",
  storeName: "user_tasks_cache",
});

/**
 * 读取缓存项；若仅有旧版 localStorage 则迁移到 IndexedDB 后删除 localStorage
 */
async function userTasksGetItem(key) {
  let v = await userTasksStorage.getItem(key);
  if (v != null) return v;
  if (typeof window === "undefined") return null;
  try {
    const legacy = localStorage.getItem(key);
    if (legacy == null) return null;
    if (key.startsWith("user_tasks_cache_v3_")) {
      try {
        v = JSON.parse(legacy);
      } catch {
        v = legacy;
      }
    } else {
      v = legacy;
    }
    await userTasksStorage.setItem(key, v);
    localStorage.removeItem(key);
    return v;
  } catch (e) {
    console.warn("user_tasks storage read/migrate failed:", key, e);
    return null;
  }
}

async function userTasksSetItem(key, value) {
  await userTasksStorage.setItem(key, value);
  if (typeof window !== "undefined") {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
}

// 使用全局认证模态框
const { openSignUpModal } = useAuthModals();

/** 判断任务是否在预测中 */
function isTaskPending(task) {
  return task.status === "starting" || task.status === "processing";
}

/** 判断是否有预测中的任务 */
const hasPendingTasks = computed(() => {
  return tasks.value.some((task) => isTaskPending(task));
});

onMounted(async () => {
  // 判断是否登录
  if (!token.value) {
    // 打开全局注册弹窗
    openSignUpModal();
    return;
  }

  if (userId.value) {
    loadTasks();
  }
});

/** 监听登录状态：登录成功后自动加载用户历史任务 */
watch(
  () => token.value,
  async (newToken, oldToken) => {
    // 当从未登录状态变为已登录状态时，加载用户历史任务
    if (newToken && !oldToken) {
      await loadTasks();
    }
  }
);

/** 当前模型 */
function selectedModel(task) {
  return modelMetaByKey.value?.[task?.model] || null;
}

function isTaskModelEnabled(task) {
  const enabled = selectedModel(task)?.enabled;
  if (enabled === undefined || enabled === null) return true;
  return !!Number(enabled);
}

/** 检测媒体类型 */
function detectMediaType(fieldDef) {
  const fmts = (fieldDef?.validate?.formats || []).join(",").toLowerCase();
  if (fmts.includes("mp3") || fmts.includes("wav") || fmts.includes("m4a")) {
    return "audio";
  }
  if (
    fmts.includes("mp4") ||
    fmts.includes("mov") ||
    fmts.includes("webm") ||
    fmts.includes("mkv")
  ) {
    return "video";
  }
  return "image";
}

/** 获取模型字段 */
const getModelFields = (modelKey) =>
  modelMetaByKey.value?.[modelKey]?.input_verify?.fields || {};

/** 获取上传字段 */
const getUploaderFieldEntries = (fields) =>
  Object.entries(fields || {}).filter(
    ([, def]) => def?.ui?.component === "uploader"
  );

/** 规范化上传值 */
const normalizeUploadValue = (value) => {
  if (Array.isArray(value)) {
    return value.filter((v) => v !== null && v !== undefined);
  }
  if (typeof value === "string") return value ? [value] : [];
  return [];
};

const resolveUserUploadValue = (value) =>
  normalizeUploadValue(value).map(resolveUserR2Url).filter(Boolean);

const stripR2UploadValue = (value) =>
  normalizeUploadValue(value).map(stripKnownR2Domain).filter(Boolean);

const getTaskOutputUrls = (task) => {
  const raw = task?.outputUrls ?? task?.outPutImageUrl;
  return resolveUserUploadValue(raw);
};

const getTaskSelectedOutputUrl = (task) => {
  const urls = getTaskOutputUrls(task);
  if (!urls.length) return null;
  const idx = taskOutputActiveIndex?.[task.id] ?? 0;
  return urls[idx] ?? urls[0];
};

/** 构建上传字段映射 */
const buildUploadFieldMapFromInput = (fields, inputJson = {}) => {
  const map = {};
  for (const [fieldKey, def] of getUploaderFieldEntries(fields)) {
    const paramsKey = def.params_key;
    if (!paramsKey) continue;
    map[fieldKey] = resolveUserUploadValue(inputJson?.[paramsKey]);
  }

  const legacyImages = inputJson?.input_images || {};
  for (const [fieldKey, legacyValue] of Object.entries(legacyImages)) {
    if (!map[fieldKey]) {
      map[fieldKey] = resolveUserUploadValue(legacyValue);
    }
  }

  const legacyVideos = resolveUserUploadValue(inputJson?.video_files);
  if (legacyVideos.length > 0) {
    const videoField = getUploaderFieldEntries(fields).find(([, def]) => {
      return detectMediaType(def) === "video";
    });
    if (videoField) {
      const [fieldKey] = videoField;
      if (!map[fieldKey] || map[fieldKey].length === 0) {
        map[fieldKey] = legacyVideos;
      }
    }
  }

  const legacyAudios = resolveUserUploadValue(inputJson?.audio_files);
  if (legacyAudios.length > 0) {
    const audioField = getUploaderFieldEntries(fields).find(([, def]) => {
      return detectMediaType(def) === "audio";
    });
    if (audioField) {
      const [fieldKey] = audioField;
      if (!map[fieldKey] || map[fieldKey].length === 0) {
        map[fieldKey] = legacyAudios;
      }
    }
  }

  return map;
};

/** 构建表单值 */
const buildFormValuesFromInput = (fields, inputJson = {}) => {
  const values = {};
  for (const [fieldKey, def] of Object.entries(fields || {})) {
    const paramsKey = def.params_key;
    if (!paramsKey) continue;
    if (def.ui.component === "uploader") {
      values[fieldKey] = resolveUserUploadValue(inputJson?.[paramsKey]);
      continue;
    }
    const raw = inputJson?.[paramsKey];
    if (raw !== undefined) values[fieldKey] = raw;
  }
  return values;
};

/** 总结上传 */
const summarizeUploads = (fields, uploadFieldMap) => {
  const inPutImageUrls = [];
  const videoFiles = [];
  const audioFiles = [];
  for (const [fieldKey, def] of getUploaderFieldEntries(fields)) {
    const urls = uploadFieldMap?.[fieldKey] || [];
    const mediaType = detectMediaType(def);
    if (mediaType === "video") {
      videoFiles.push(...urls);
    } else if (mediaType === "audio") {
      audioFiles.push(...urls);
    } else {
      inPutImageUrls.push(...urls);
    }
  }
  return { inPutImageUrls, videoFiles, audioFiles };
};

/** 根据字段定义和 inputJson 构建任务数据（底层通用实现） */
const buildTaskFromFields = (fields, inputJson, baseTask = {}) => {
  const formValues = buildFormValuesFromInput(fields, inputJson);
  const uploadFieldMap = buildUploadFieldMapFromInput(fields, inputJson);
  const { inPutImageUrls, videoFiles, audioFiles } = summarizeUploads(
    fields,
    uploadFieldMap
  );

  const outputFormatFallback =
    inputJson?.output_format || inputJson?.output_image_format;
  if (outputFormatFallback && formValues.outputImageFormat === undefined) {
    formValues.outputImageFormat = outputFormatFallback;
  }

  return {
    ...baseTask,
    ...formValues,
    uploadFieldMap,
    inPutImageUrls,
    videoFiles,
    audioFiles,
  };
};

/** 普通 model 类型任务：通过 modelKey 查找字段定义 */
const buildTaskFromInputJson = (modelKey, inputJson, baseTask = {}) => {
  const fields = getModelFields(modelKey);
  return buildTaskFromFields(fields, inputJson, baseTask);
};

/** effects 类型任务：通过 task.effect（id）或 code 查找字段定义 */
const buildEffectTaskFromInputJson = (inputJson, baseTask = {}) => {
  const fields = getEffectFields(baseTask);
  return buildTaskFromFields(fields, inputJson, baseTask);
};

/**
 * 格式化时间，自动根据用户时区和本地习惯显示
 */
function formatTime(timestamp) {
  // 获取用户的语言环境（默认使用浏览器语言）
  const userLocale = navigator.language || "en-US";
  // 创建Date对象
  const date = new Date(timestamp);

  // 根据用户的语言环境和时区进行格式化
  const formatter = new Intl.DateTimeFormat(userLocale, {
    year: "numeric",
    month: "long", // 可以更改为'short'来显示简短月份名称
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  // 格式化并返回
  return formatter.format(date);
}

// 获取菜单数据
const menuData = inject("menuData", ref([]));

// 获取 任务配置
async function getTaskConfig() {
  const { data: taskConfigData } = await useAsyncData(
    `taskConfigData`,
    async () => {
      const apiUrl = `/api/r2/ai/pages/my_task.json`;

      try {
        let response = await r2CachedFetch(apiUrl);

        // 如果 response 为空，返回 null
        if (!response) {
          return null;
        }
        return response;
      } catch (error) {
        // 捕获 fetch 错误（如 404），返回 null
        console.error("Fetch error:", error);
        return null;
      }
    }
  );
  return taskConfigData.value;
}

/**
 * 从路径中提取工具键
 */
function toolKeyFromPath(path) {
  const last = (path || "").split("/").pop() || "";
  if (!last.includes("?")) return last;
  const query = last.split("?")[1] || "";
  const params = new URLSearchParams(query);
  return params.get("tool") || last;
}

function flattenMenuItems() {
  const rows = [];
  for (const group of Array.isArray(menuData.value) ? menuData.value : []) {
    for (const item of Array.isArray(group?.children) ? group.children : []) {
      rows.push({ group, item });
    }
  }
  return rows;
}

/**
 * 将分组名称转换为分组键
 * 支持的分组：Image AI -> image, Video AI -> video, Audio AI -> audio, AI Models -> models
 * 其他分组名称会尝试从路径中推断（如 "image/xxx" -> "image"）
 */
function groupNameToKey(groupName) {
  if (!groupName) return "other";

  // 标准分组名称映射
  const groupNameMap = {
    "AI Effects": "effects",
    "Image AI": "image",
    "Video AI": "video",
    "Audio AI": "audio",
    "AI Models": "models",
  };

  // 如果直接匹配，返回映射值
  if (groupNameMap[groupName]) {
    return groupNameMap[groupName];
  }

  // 尝试从分组名称中提取关键词（兼容性处理）
  const lowerName = groupName.toLowerCase();
  if (lowerName.includes("image")) return "image";
  if (lowerName.includes("video")) return "video";
  if (lowerName.includes("audio")) return "audio";
  if (lowerName.includes("model")) return "models";
  if (lowerName.includes("effect")) return "effects";

  return "other";
}

/**
 * 工具元数据映射表（基于路径）
 * key: 路径（如 "image/text-to-image"）
 * value: { label, icon, groupName, groupIcon }
 */
const toolMetaMap = computed(() => {
  return flattenMenuItems().reduce((acc, { group, item }) => {
    const url = normalizeCatalogUrl(item?.url);
    if (!url) return acc;

    acc[url] = {
      label: item?.title || item?.ref || url,
      icon: item?.icon,
      groupName: group?.title,
      groupIcon: group?.icon,
    };
    return acc;
  }, {});
});

/**
 * 工具名称到路径的映射缓存
 */
const toolNameToUrlMap = computed(() => {
  return flattenMenuItems().reduce((acc, { item }) => {
    const toolName = item?.ref;
    const url = normalizeCatalogUrl(item?.url);
    if (toolName && url) {
      acc[toolName] = url;
    }
    return acc;
  }, {});
});

/**
 * Effects 配置列表（video + image 合并；同 code 可共存，靠 id 区分）
 */
const effectsConfigList = computed(() => {
  const v = effectsByTool.value;
  if (!v) return [];

  try {
    if (typeof v === "string") {
      const parsed = JSON.parse(v);
      return normalizeEffectsList(parsed);
    }
    if (Array.isArray(v)) return v;
    if (v.video != null || v.image != null) {
      return [
        ...normalizeEffectsList(v.video || []),
        ...normalizeEffectsList(v.image || []),
      ];
    }
    if (typeof v === "object") {
      return Object.values(v).flat();
    }
  } catch (error) {
    console.error("解析 effectsByTool 数据失败:", error);
  }

  return [];
});

/** 按 effect 表 id 索引（接口 res.data.effect 与此对应） */
const effectsConfigById = computed(() => {
  return (effectsConfigList.value || []).reduce((acc, effect) => {
    const id = effect?.id;
    if (id != null && id !== "") {
      acc[String(id)] = effect;
    }
    return acc;
  }, {});
});

/**
 * 仅按 code 查找（无 effect id 的旧数据兜底；同 code 多条时取列表中第一条）
 */
function getEffectConfigByToolName(toolName) {
  if (!toolName) return null;
  return effectsConfigList.value.find((e) => e?.code === toolName) || null;
}

const effectDetailCache = useState("ai-effects-detail-cache", () => ({}));

function getEffectDetailCacheKey(effectMeta) {
  if (!effectMeta?.effect_type || !effectMeta?.code) return "";
  return `${effectMeta.effect_type}:${effectMeta.code}`;
}

async function loadEffectDetailForTask(task) {
  const effectMeta = getEffectConfigForTask(task);
  const cacheKey = getEffectDetailCacheKey(effectMeta);
  if (!cacheKey) return effectMeta || null;
  if (effectDetailCache.value[cacheKey]) return effectDetailCache.value[cacheKey];

  try {
    const detail = await r2CachedFetch(
      `/api/r2/ai/effects/${effectMeta.effect_type}/configs/${effectMeta.code}.json`
    );
    if (detail && typeof detail === "object") {
      effectDetailCache.value = {
        ...effectDetailCache.value,
        [cacheKey]: detail,
      };
      return detail;
    }
  } catch (error) {
    console.error("加载 effect detail 失败:", error);
  }

  return effectMeta || null;
}

/**
 * 任务对应的 effect 配置：优先用 task.effect（id），否则按 toolName/code 兜底
 */
function getEffectConfigForTask(task) {
  if (!task) return null;
  const raw = task.effect;
  if (raw != null && raw !== "") {
    const byId = effectsConfigById.value[String(raw)];
    if (byId) return byId;
  }
  if (task.toolName) {
    return getEffectConfigByToolName(task.toolName);
  }
  return null;
}

/** 获取 effect 字段（优先用 effect id 匹配） */
const getEffectFields = (task) => {
  const cfg = getLoadedEffectConfigForTask(task);
  return cfg?.input_verify?.fields || {};
};

function getLoadedEffectConfigForTask(task) {
  const effectMeta = getEffectConfigForTask(task);
  const cacheKey = getEffectDetailCacheKey(effectMeta);
  return (cacheKey && effectDetailCache.value[cacheKey]) || effectMeta || null;
}

async function buildEffectTaskFromInputJsonAsync(inputJson, baseTask = {}) {
  const cfg = await loadEffectDetailForTask(baseTask);
  const fields = cfg?.input_verify?.fields || {};
  return buildTaskFromFields(fields, inputJson, baseTask);
}

/** Vuex 历史缓存 key：有 effect id 时用 effect:id，避免同 code 混桶 */
function historyCacheKeyForTask(task) {
  if (!task) return "";
  if (task.effect != null && task.effect !== "") {
    return `effect:${String(task.effect)}`;
  }
  return task.toolName || "";
}

function getEffectTabKey(task) {
  return historyCacheKeyForTask(task);
}

const toolOrder = computed(() => {
  return flattenMenuItems()
    .map(({ item }) => item?.ref || toolKeyFromPath(item?.url))
    .filter(Boolean);
});

/**
 * 根据工具名称获取对应的路径
 */
function toolUrlKey(toolName) {
  if (!toolName) return null;
  return toolNameToUrlMap.value[toolName] || null;
}

/**
 * 根据工具名称获取工具元数据（标题、图标等）
 * 支持从 menuData 和 effectsByTool 中获取
 */
function toolTitle(toolName, task = null) {
  if (!toolName && !(task && isEffectTask(task))) {
    return { label: "", icon: "i-lucide-sparkles" };
  }

  if (task && isEffectTask(task)) {
    const effectConfig = getEffectConfigForTask(task);
    if (effectConfig?.title) {
      return {
        label: effectConfig.title,
        icon: effectConfig.cover_url ? undefined : "i-lucide-sparkles",
      };
    }
  }

  if (!toolName) {
    return { label: "", icon: "i-lucide-sparkles" };
  }

  // 从 menuData 中查找
  const url = toolUrlKey(toolName);
  if (!url) {
    return { label: toolName, icon: "i-lucide-sparkles" };
  }

  return (
    toolMetaMap.value[url] || { label: toolName, icon: "i-lucide-sparkles" }
  );
}

/**
 * 判断任务是否是 effects 类型
 * 新数据：接口返回 effect（id）非空即为 effect 任务；旧缓存：无 effect 时仍用 model 空 + toolName 判断
 */
function isEffectTask(task) {
  if (!task) return false;
  if (task.effect != null && task.effect !== "") return true;
  return (
    (!task.model || task.model === "") &&
    task.toolName &&
    task.toolName.trim() !== ""
  );
}

/**
 * 根据工具名称获取分组键
 * 优先级：1. 如果是 effects 类型任务，返回 "effects"
 *         2. 从 menuData 中查找 groupName 并转换
 *         3. 从路径中推断（如 "image/xxx" -> "image"）
 *         4. 返回 "other"
 */
function toolGroupKey(toolName, task = null) {
  if (!toolName) return "other";

  // 方法1: 如果是 effects 类型任务，返回 "effects"
  if (task && isEffectTask(task)) {
    return "effects";
  }

  // 方法2: 从 menuData 中查找
  const menuRow = flattenMenuItems().find(({ item }) => item?.ref === toolName);
  if (menuRow?.group?.title) {
    const groupKey = groupNameToKey(menuRow.group.title);
    if (groupKey !== "other") {
      return groupKey;
    }
  }

  // 方法3: 从路径中推断（如果 menuData 中没有找到或返回了 "other"）
  const url = toolUrlKey(toolName);
  if (url) {
    const parts = url.split("/").filter(Boolean);
    const key = String(parts[1] || parts[0] || "").toLowerCase();
    if (["image", "video", "audio", "models", "effects"].includes(key)) {
      return key;
    }
  }

  // 方法4: 默认返回 "other"
  return "other";
}

/**
 * 获取任务配置的 key（用于从 taskConfigData 中获取配置）
 * 格式：${groupKey}/${toolName}
 * 对于 effects 类型：${effect_type}/${toolName}
 * 对于普通类型：${toolGroupKey}/${toolName}
 */
function getTaskConfigKey(task) {
  if (!task) return null;

  if (isEffectTask(task)) {
    const effectConfig = getEffectConfigForTask(task);
    const code = effectConfig?.code || task.toolName;
    const et = effectConfig?.effect_type;
    if (et && code) {
      return `${et}/${code}`;
    }
  }

  if (!task?.toolName) return null;

  return toolUrlKey(task.toolName)?.replace(/^ai\//, "") || null;
}

/**
 * 获取任务对应的导航路径（用于 navigateTo 跳转）
 * - effects 任务：/ai/effects/${effect_type}/${toolName}/
 * - 普通任务：优先使用 menuData 中的 path，否则 /ai/${groupKey}/${toolName}/
 */
function getTaskNavigatePath(task) {
  if (!task) return null;

  if (isEffectTask(task)) {
    const effectConfig = getEffectConfigForTask(task);
    const code = effectConfig?.code || task.toolName;
    if (!code) return null;
    const effectType = effectConfig?.effect_type || "image";
    return `/ai/effects/${effectType}/${code}/`;
  }

  if (!task?.toolName) return null;

  // 普通任务：优先使用 menuData 中的完整 path（如 "image/text-to-image"）
  const menuUrl = toolUrlKey(task.toolName);
  if (menuUrl) {
    return toInternalRoute(menuUrl);
  }

  // 兜底：用 groupKey 拼接
  const groupKey = toolGroupKey(task.toolName, task);
  return `/ai/${groupKey}/${task.toolName}/`;
}

/**
 * 获取任务对应的页面链接（用于超链接展示）
 * - effects 任务：/ai/effects/${effect_type}/${toolName}/
 * - model 任务：/ai/${type}/${toolName}/?model=${model}
 * - 普通任务：/ai/${path}/
 */
function getTaskPageUrl(task) {
  if (!task) return null;

  if (isEffectTask(task)) {
    const effectConfig = getEffectConfigForTask(task);
    const code = effectConfig?.code || task.toolName;
    if (!code) return null;
    const effectType = effectConfig?.effect_type || "image";
    return `/ai/effects/${effectType}/${code}/`;
  }

  if (!task?.toolName) return null;

  // 有模型信息的任务：带 model 参数
  const menuUrl = toolUrlKey(task.toolName);
  const basePath = menuUrl
    ? toInternalRoute(menuUrl)
    : `/ai/${toolGroupKey(task.toolName, task)}/${task.toolName}/`;

  if (task.model) {
    return `${basePath}?model=${task.modelName}`;
  }

  return basePath;
}

/**
 * 分组配置映射表
 * 定义每个分组键对应的显示标签和图标
 */
const GROUP_CONFIG = {
  image: {
    label: "Image AI",
    icon: "material-symbols-light:imagesmode-outline-rounded",
  },
  video: {
    label: "Video AI",
    icon: "material-symbols-light:video-library-outline-rounded",
  },
  audio: {
    label: "Audio AI",
    icon: "material-symbols-light:speech-to-text-rounded",
  },
  models: {
    label: "AI Models",
    icon: "material-symbols-light:psychology-outline-rounded",
  },
  effects: {
    label: "AI Effects",
    icon: "i-lucide-sparkles",
  },
  other: {
    label: "Other",
    icon: "material-symbols-light:category-outline-rounded",
  },
};

/**
 * 分组标签项列表
 * 根据实际任务数据动态生成，只显示有任务的分组
 */
const groupTabItems = computed(() => {
  // 统计每个分组的任务数量
  const counts = (tasks.value || []).reduce((acc, t) => {
    const key = toolGroupKey(t?.toolName, t);
    if (!acc[key]) acc[key] = 0;
    acc[key] += 1;
    return acc;
  }, {});

  const items = [
    {
      label: "All",
      value: "all",
      icon: "material-symbols-light:border-all-rounded",
    },
  ];

  // 按预定义顺序添加有任务的分组
  const orderedGroups = [
    "image",
    "video",
    "audio",
    "models",
    "effects",
    "other",
  ];
  orderedGroups.forEach((groupKey) => {
    if (counts[groupKey] > 0 && GROUP_CONFIG[groupKey]) {
      items.push({
        label: GROUP_CONFIG[groupKey].label,
        value: groupKey,
        icon: GROUP_CONFIG[groupKey]?.icon,
      });
    }
  });

  return items;
});

const showGroupTabs = computed(() => groupTabItems.value.length > 2);

/** 依据内容动态生成 Tabs 项 */
const toolTabItems = computed(() => {
  const baseTasks =
    groupTab.value === "all"
      ? tasks.value
      : tasks.value.filter(
          (t) => toolGroupKey(t.toolName, t) === groupTab.value
        );

  const tabKeys = Array.from(
    new Set(baseTasks.map((t) => getEffectTabKey(t)))
  ).filter(Boolean);

  const dynamicItems = tabKeys.map((key) => {
    const sampleTask = baseTasks.find((t) => getEffectTabKey(t) === key);
    const meta = toolTitle(sampleTask?.toolName || "", sampleTask);
    return {
      label: meta?.label,
      icon: meta?.icon || "i-lucide-sparkles",
      value: key,
    };
  });

  return [
    {
      label: "All",
      value: "all",
      icon: "material-symbols-light:border-all-rounded",
    },
    ...dynamicItems,
  ];
});

const showToolTabs = computed(() => toolTabItems.value.length > 2);

const modelMetaByKey = computed(() => {
  const index = {};
  for (const [toolName, titlesObj] of Object.entries(
    modelsByTool.value || {}
  )) {
    for (const [modelTitle, versions] of Object.entries(titlesObj || {})) {
      for (const item of versions || []) {
        if (!item?.key) continue;
        index[item.key] = {
          ...item,
          toolName,
          modelTitle,
          versionLabel: item.label,
        };
      }
    }
  }
  return index;
});

function taskMatchesSelectedGroup(task) {
  return (
    groupTab.value === "all" ||
    toolGroupKey(task.toolName, task) === groupTab.value
  );
}

function taskMatchesSelectedTool(task) {
  return toolTab.value === "all" || getEffectTabKey(task) === toolTab.value;
}

function taskModelTitle(task) {
  return modelMetaByKey.value?.[task?.model]?.modelTitle || task?.model || "";
}

function selectedModelRatio(task) {
  const options =
    modelMetaByKey.value?.[task?.model]?.input_verify?.fields?.ratio?.options ||
    getEffectFields(task)?.ratio?.options;
  if (!options) return task?.ratio || "";
  const option = options.find((o) => String(o.key) === String(task?.ratio));
  return option?.label || task?.ratio || "";
}

function taskMatchesSelectedModelTitle(task) {
  return (
    modelTitleTab.value === "all" ||
    taskModelTitle(task) === modelTitleTab.value
  );
}

function taskMatchesSelectedModelVersion(task) {
  return (
    modelVersionTab.value === "all" || task?.model === modelVersionTab.value
  );
}

const modelTitleTabItems = computed(() => {
  if (toolTab.value === "all") return [];

  const baseTasks = tasks.value
    .filter((t) => taskMatchesSelectedGroup(t))
    .filter((t) => taskMatchesSelectedTool(t));

  const titlesInTasks = Array.from(
    new Set(baseTasks.map((t) => taskModelTitle(t)))
  ).filter(Boolean);

  const configuredTitles = Object.keys(
    modelsByTool.value?.[toolTab.value] || {}
  );
  const orderedTitles = Array.from(
    new Set([
      ...configuredTitles.filter((t) => titlesInTasks.includes(t)),
      ...titlesInTasks.filter((t) => !configuredTitles.includes(t)),
    ])
  );

  const dynamicItems = orderedTitles.map((title) => {
    const first = (modelsByTool.value?.[toolTab.value]?.[title] || [])[0];
    const avatarSrc = first?.avatar?.src;
    return {
      label: title,
      value: title,
      avatar: avatarSrc
        ? {
            src: avatarSrc,
            alt: title,
          }
        : undefined,
    };
  });

  return [
    {
      label: "All",
      value: "all",
      icon: "material-symbols-light:border-all-rounded",
    },
    ...dynamicItems,
  ];
});

const showModelTitleTabs = computed(() => modelTitleTabItems.value.length > 2);

const modelVersionTabItems = computed(() => {
  if (toolTab.value === "all" || modelTitleTab.value === "all") return [];

  const baseTasks = tasks.value
    .filter((t) => taskMatchesSelectedGroup(t))
    .filter((t) => taskMatchesSelectedTool(t))
    .filter((t) => taskMatchesSelectedModelTitle(t));
  const keysInTasks = new Set(baseTasks.map((t) => t?.model).filter(Boolean));

  const versions =
    modelsByTool.value?.[toolTab.value]?.[modelTitleTab.value] || [];
  const configured = (Array.isArray(versions) ? versions : []).filter(
    (v) => v?.key && keysInTasks.has(v.key)
  );
  const configuredKeys = new Set(configured.map((v) => v.key));

  const extraKeys = Array.from(keysInTasks).filter(
    (k) => !configuredKeys.has(k)
  );

  const dynamicItems = [
    ...configured.map((v) => ({
      label: v.label || v.key,
      value: v.key,
      avatar: v?.avatar?.src
        ? {
            src: v.avatar.src,
            alt: v.label || v.key,
          }
        : undefined,
    })),
    ...extraKeys.map((key) => {
      const meta = modelMetaByKey.value?.[key];
      return {
        label: meta?.versionLabel || meta?.label || key,
        value: key,
        avatar: meta?.avatar?.src
          ? {
              src: meta.avatar.src,
              alt: meta?.versionLabel || meta?.label || key,
            }
          : undefined,
      };
    }),
  ];

  if (dynamicItems.length <= 1) return [];

  return [
    {
      label: "All",
      value: "all",
      icon: "material-symbols-light:border-all-rounded",
    },
    ...dynamicItems,
  ];
});

const showModelVersionTabs = computed(
  () => modelVersionTabItems.value.length > 2
);

const selectedGroupTabItem = computed(() => {
  return groupTabItems.value.find((i) => i.value === groupTab.value);
});

const selectedToolTabItem = computed(() => {
  return toolTabItems.value.find((i) => i.value === toolTab.value);
});

const selectedModelTitleTabItem = computed(() => {
  return modelTitleTabItems.value.find((i) => i.value === modelTitleTab.value);
});

const selectedModelVersionTabItem = computed(() => {
  return modelVersionTabItems.value.find(
    (i) => i.value === modelVersionTab.value
  );
});

function normalizeTaskSearchText(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function taskModelVersionLabel(task) {
  const meta = modelMetaByKey.value?.[task?.model];
  return meta?.versionLabel || meta?.label || task?.model || "";
}

function getTaskSearchValues(task) {
  if (!task) return [];

  const groupKey = toolGroupKey(task.toolName, task);
  const groupLabel = GROUP_CONFIG[groupKey]?.label || "";
  const toolMeta = toolTitle(task.toolName, task);
  const modelTitle = taskModelTitle(task);
  const modelVersion = taskModelVersionLabel(task);

  return [
    task.id,
    task.prompt,
    task.toolName,
    getEffectTabKey(task),
    toolMeta?.label,
    groupKey,
    groupLabel,
    task.model,
    modelTitle,
    modelVersion,
  ]
    .filter((value) => value !== null && value !== undefined && value !== "")
    .map((value) => normalizeTaskSearchText(value));
}

function taskMatchesSearchQuery(task) {
  const query = normalizeTaskSearchText(taskSearchQuery.value);
  if (!query) return true;
  return getTaskSearchValues(task).some((value) => value.includes(query));
}

async function applyTaskSearch() {
  const normalizedQuery = (taskSearchInput.value || "").trim();
  if (normalizedQuery === taskSearchQuery.value) return;
  taskSearchQuery.value = normalizedQuery;
  await filterTasksAsync();
}

async function clearTaskSearch() {
  const hadInput = Boolean(taskSearchInput.value);
  const hadQuery = Boolean(taskSearchQuery.value);
  taskSearchInput.value = "";
  if (hadQuery) {
    taskSearchQuery.value = "";
    await filterTasksAsync();
    return;
  }
  if (hadInput) {
    await nextTick();
  }
}

// 异步筛选任务列表，避免UI阻塞
const filteredTasks = ref([]);
const isFiltering = ref(false);
const filterTimeout = ref(null);

// 分页相关
const page = ref(1);
const itemsPerPage = ref(24);
const evenItemsPerPage = computed(() => {
  const n = Number(itemsPerPage.value) || 10;
  return n % 2 === 0 ? n : n + 1;
});
const pagedTasks = computed(() => {
  const start = (page.value - 1) * evenItemsPerPage.value;
  return (filteredTasks.value || []).slice(
    start,
    start + evenItemsPerPage.value
  );
});

const cardsContainerRef = ref(null);

function scrollToTop() {
  const el = cardsContainerRef?.value;
  if (el && typeof el.scrollTo === "function") {
    el.scrollTo({ top: 0, behavior: "smooth" });
  } else if (typeof window !== "undefined") {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
}

function onPageChange(newPage) {
  page.value = newPage;
  nextTick(() => scrollToTop());
}

/** 异步筛选任务列表，避免UI阻塞 */
async function filterTasksAsync() {
  // 清除之前的定时器
  if (filterTimeout.value) {
    clearTimeout(filterTimeout.value);
  }

  // 设置筛选状态
  isFiltering.value = true;

  // 使用 setTimeout 将筛选操作放到下一个事件循环中执行
  await new Promise((resolve) => {
    filterTimeout.value = setTimeout(() => {
      let result = tasks.value;

      // 一级：按分组筛选
      result = result.filter((t) => taskMatchesSelectedGroup(t));

      // 二级：按功能筛选
      result = result.filter((t) => taskMatchesSelectedTool(t));

      // 三级：按模型标题筛选
      result = result.filter((t) => taskMatchesSelectedModelTitle(t));

      // 四级：按模型版本筛选
      result = result.filter((t) => taskMatchesSelectedModelVersion(t));

      // 五级：按 task id / prompt / tabs 相关值搜索
      result = result.filter((t) => taskMatchesSearchQuery(t));

      filteredTasks.value = result;
      // 重置到第1页，确保筛选作用于全量数据后再分页
      page.value = 1;
      isFiltering.value = false;
      resolve();
    }, 5); // 更短的延迟，让UI响应更流畅
  });
}

/** 初始化筛选任务列表 */
function initializeFilteredTasks() {
  filteredTasks.value = tasks.value;
  page.value = 1;
}

function extractHistoryResponse(payload) {
  if (Array.isArray(payload)) {
    return { items: payload, targetUser: null };
  }
  return {
    items: Array.isArray(payload?.items) ? payload.items : [],
    targetUser: payload?.target_user || null,
  };
}

function formatCreditValue(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "0";
  return Number.isInteger(num)
    ? String(num)
    : num.toFixed(2).replace(/\.?0+$/, "");
}

async function applyAdminTaskFilter() {
  const normalizedValue = prepareAdminQuerySubmit(
    adminQueryInput,
    adminQueryType
  );
  if (
    adminQueryType.value === activeAdminQueryType.value &&
    normalizedValue === activeAdminQueryValue.value &&
    (normalizedValue ||
      adminTargetUserSummary.value ||
      adminTargetUserNotFound.value)
  ) {
    await loadTasks(true);
    return;
  }
  activeAdminQueryType.value = normalizedValue ? adminQueryType.value : "";
  activeAdminQueryValue.value = normalizedValue;
  adminTargetUserSummary.value = null;
  adminTargetUserNotFound.value = false;
  await loadTasks(true);
}

async function clearAdminTaskFilter() {
  const hadActiveFilter = Boolean(activeAdminQueryValue.value);
  adminQueryInput.value = "";
  activeAdminQueryType.value = "";
  activeAdminQueryValue.value = "";
  adminTargetUserSummary.value = null;
  adminTargetUserNotFound.value = false;
  if (hadActiveFilter) {
    await loadTasks(true);
  }
}

/** 监听groupTab变化，异步筛选任务 */
watch(
  () => groupTab.value,
  async (newTab, oldTab) => {
    if (newTab !== oldTab) {
      toolTab.value = "all";
      modelTitleTab.value = "all";
      modelVersionTab.value = "all";
      await filterTasksAsync();
    }
  }
);

/** 监听toolTab变化，异步筛选任务 */
watch(
  () => toolTab.value,
  async (newTab, oldTab) => {
    if (newTab !== oldTab) {
      modelTitleTab.value = "all";
      modelVersionTab.value = "all";
      await filterTasksAsync();
    }
  }
);

/** 监听modelTitleTab变化，异步筛选任务 */
watch(
  () => modelTitleTab.value,
  async (newTab, oldTab) => {
    if (newTab !== oldTab) {
      modelVersionTab.value = "all";
      await filterTasksAsync();
    }
  }
);

/** 监听modelVersionTab变化，异步筛选任务 */
watch(
  () => modelVersionTab.value,
  async (newTab, oldTab) => {
    if (newTab !== oldTab) {
      await filterTasksAsync();
    }
  }
);

/** 当可用的 tabs 改变时，若当前 tab 不存在则回退到 all */
watch(
  () => groupTabItems.value,
  (items) => {
    if (items.length <= 2 && groupTab.value !== "all") groupTab.value = "all";
    const exist = items.some((i) => i.value === groupTab.value);
    if (!exist) groupTab.value = "all";
  },
  { deep: true }
);

/** 监听toolTabItems变化，若当前tab不存在则回退到 all */
watch(
  () => toolTabItems.value,
  (items) => {
    if (items.length <= 2 && toolTab.value !== "all") toolTab.value = "all";
    const exist = items.some((i) => i.value === toolTab.value);
    if (!exist) toolTab.value = "all";
  },
  { deep: true }
);

/** 监听modelTitleTabItems变化，若当前tab不存在则回退到 all */
watch(
  () => modelTitleTabItems.value,
  (items) => {
    if (items.length <= 2 && modelTitleTab.value !== "all") {
      modelTitleTab.value = "all";
    }
    const exist = items.some((i) => i.value === modelTitleTab.value);
    if (!exist) modelTitleTab.value = "all";
  },
  { deep: true }
);

/** 监听modelVersionTabItems变化，若当前tab不存在则回退到 all */
watch(
  () => modelVersionTabItems.value,
  (items) => {
    if (items.length <= 2 && modelVersionTab.value !== "all") {
      modelVersionTab.value = "all";
    }
    const exist = items.some((i) => i.value === modelVersionTab.value);
    if (!exist) modelVersionTab.value = "all";
  },
  { deep: true }
);

/** 窗口重新获得焦点时，检查数据是否过期并刷新 */
const focused = useWindowFocus();
watch(focused, async (isFocused) => {
  if (isFocused && token.value) {
    const TIME_KEY = `user_tasks_time_v3_${taskCacheScope.value}`;
    const savedTime = await userTasksGetItem(TIME_KEY);
    const ts = savedTime != null ? parseInt(String(savedTime), 10) : NaN;
    // 检查时间是否存在且超过5分钟且是否是第一页
    if (
      !Number.isNaN(ts) &&
      Date.now() - ts > 5 * 60 * 1000 &&
      page.value === 1
    ) {
      loadTasks(true); // 强制刷新
    }
  }
});

/** 加载全部历史任务 */
async function loadTasks(force = false) {
  loading.value = true;
  const CACHE_KEY = `user_tasks_cache_v3_${taskCacheScope.value}`;
  const TIME_KEY = `user_tasks_time_v3_${taskCacheScope.value}`;
  const requestedAdminQueryType =
    isAdmin.value && activeAdminQueryType.value
      ? activeAdminQueryType.value
      : "";
  const requestedAdminQueryValue =
    isAdmin.value && activeAdminQueryValue.value
      ? activeAdminQueryValue.value
      : "";

  try {
    let allTasks = null;
    let usedCache = false;
    let targetUserSummary = requestedAdminQueryValue
      ? adminTargetUserSummary.value
      : null;

    // 尝试从缓存读取
    const savedTimeRaw = await userTasksGetItem(TIME_KEY);
    const savedTime = savedTimeRaw != null ? String(savedTimeRaw) : "";
    if (savedTime) {
      lastUpdateTime.value = formatTime(parseInt(savedTime, 10));
    }

    if (
      !force &&
      savedTime &&
      Date.now() - parseInt(savedTime, 10) < 5 * 60 * 1000
    ) {
      const savedData = await userTasksGetItem(CACHE_KEY);
      if (savedData) {
        try {
          const parsedCache =
            typeof savedData === "string" ? JSON.parse(savedData) : savedData;
          const cachedPayload = Array.isArray(parsedCache)
            ? { items: parsedCache, targetUser: null }
            : {
                items: Array.isArray(parsedCache?.items)
                  ? parsedCache.items
                  : [],
                targetUser:
                  parsedCache?.targetUser || parsedCache?.target_user || null,
              };
          allTasks = cachedPayload.items;
          targetUserSummary = cachedPayload.targetUser;
          if (Array.isArray(allTasks)) {
            usedCache = true;
            adminTargetUserSummary.value = requestedAdminQueryValue
              ? targetUserSummary
              : null;
            adminTargetUserNotFound.value = Boolean(
              requestedAdminQueryValue && !targetUserSummary
            );
            console.log("Loaded tasks from cache");
          }
        } catch (e) {
          console.error("Failed to parse cached tasks", e);
        }
      }
    }

    if (!usedCache) {
      const res = await store.dispatch("ai/getUserHistory", {
        userId: userId.value,
        is_all: 1,
        tool_name: "",
        admin_query_type: requestedAdminQueryType,
        admin_query_value: requestedAdminQueryValue,
      });
      const historyPayload = extractHistoryResponse(res?.data);
      if (res?.status === 200 && Array.isArray(historyPayload.items)) {
        targetUserSummary = historyPayload.targetUser;
        adminTargetUserSummary.value = requestedAdminQueryValue
          ? targetUserSummary
          : null;
        adminTargetUserNotFound.value = Boolean(
          requestedAdminQueryValue && !targetUserSummary
        );
        const parseMaybeJson = (value, fallback) => {
          if (value === null || value === undefined || value === "") {
            return fallback;
          }
          if (typeof value === "string") {
            try {
              return JSON.parse(value);
            } catch (error) {
              return fallback;
            }
          }
          if (typeof value === "object") return value;
          return fallback;
        };

        const extractLegacyInputImages = (inputJson) => {
          if (!inputJson?.input_images) return [];
          return Object.values(inputJson.input_images)
            .flatMap((images) =>
              Array.isArray(images)
                ? images.filter((img) => img !== null && img !== undefined)
                : typeof images === "string"
                ? [images]
                : []
            )
            .filter((img) => img !== null && img !== undefined)
            .map(resolveUserR2Url)
            .filter(Boolean);
        };

        // 将API返回的数据转换为任务列表格式
        allTasks = await Promise.all(
          historyPayload.items.map(async (item) => {
          const inputPayload = parseMaybeJson(item.input, {});
          const inputJson =
            inputPayload && !Array.isArray(inputPayload) ? inputPayload : {};
          const outputPayload = parseMaybeJson(item.output, []);
          const outputUrls = Array.isArray(outputPayload)
            ? outputPayload
            : normalizeUploadValue(outputPayload);
          const normalizedOutputUrls = resolveUserUploadValue(outputUrls);
          const outPutImageUrl = normalizedOutputUrls[0] || null;

          const toolName = item.tool_name || item.toolName || "";
          const rawEffect = item.effect;
          const hasEffectId = rawEffect != null && rawEffect !== "";
          const isEffect = hasEffectId;

          const baseTask = {
            id: item.id,
            model: item.model,
            modelName:
              modelMetaByKey.value?.[item.model]?.label
                .toLowerCase()
                .replace(/\s+/g, "-") || "",
            effect: hasEffectId ? rawEffect : null,
            status: item.status || "succeeded",
            toolName,
            isPublic:
              item.isPublic === "1" ||
              item.isPublic === 1 ||
              item.isPublic === true,
            isArchived:
              item.isArchived === "1" ||
              item.isArchived === 1 ||
              item.isArchived === true,
            outPutImageUrl: outPutImageUrl,
            outputUrls: normalizedOutputUrls,
            createdAt: new Date(
              /\d{2}:\d{2}:\d{2}$/.test(item.completed_at)
                ? item.completed_at + "Z"
                : item.completed_at
            ).getTime(),
            creditCost: item.creditCost,
          };

          // 根据任务类型使用不同的字段定义来解析 inputJson
          const task = isEffect
            ? await buildEffectTaskFromInputJsonAsync(inputJson, baseTask)
            : buildTaskFromInputJson(item.model, inputJson, baseTask);

          if (task.prompt === undefined && inputJson.prompt !== undefined) {
            task.prompt = inputJson.prompt;
          }
          if (
            task.ratio === undefined &&
            inputJson.aspect_ratio !== undefined
          ) {
            task.ratio = inputJson.aspect_ratio;
          }
          if (task.duration === undefined && inputJson.duration !== undefined) {
            task.duration = inputJson.duration;
          }
          if (
            task.resolution === undefined &&
            inputJson.resolution !== undefined
          ) {
            task.resolution = inputJson.resolution;
          }
          if (
            task.generateAudio === undefined &&
            inputJson.generate_audio !== undefined
          ) {
            task.generateAudio = inputJson.generate_audio;
          }
          if (task.lyrics === undefined && inputJson.lyrics !== undefined) {
            task.lyrics = inputJson.lyrics;
          }
          if (task.bitrate === undefined && inputJson.bitrate !== undefined) {
            task.bitrate = inputJson.bitrate;
          }
          if (
            task.sampleRate === undefined &&
            inputJson.sample_rate !== undefined
          ) {
            task.sampleRate = inputJson.sample_rate;
          }
          if (
            task.outputImageFormat === undefined &&
            (inputJson.output_format || inputJson.output_image_format)
          ) {
            task.outputImageFormat =
              inputJson.output_format || inputJson.output_image_format;
          }

          const legacyImageUrls = extractLegacyInputImages(inputJson);
          if (!Array.isArray(task.inPutImageUrls)) {
            task.inPutImageUrls = resolveUserUploadValue(task.inPutImageUrls);
          }
          if (task.inPutImageUrls.length === 0 && legacyImageUrls.length > 0) {
            task.inPutImageUrls = legacyImageUrls;
          }

          if (!Array.isArray(task.videoFiles)) {
            task.videoFiles = resolveUserUploadValue(task.videoFiles);
          }
          const legacyVideos = resolveUserUploadValue(inputJson.video_files);
          if (task.videoFiles.length === 0 && legacyVideos.length > 0) {
            task.videoFiles = legacyVideos;
          }

          if (!Array.isArray(task.audioFiles)) {
            task.audioFiles = resolveUserUploadValue(task.audioFiles);
          }
          const legacyAudios = resolveUserUploadValue(inputJson.audio_files);
          if (task.audioFiles.length === 0 && legacyAudios.length > 0) {
            task.audioFiles = legacyAudios;
          }

            return task;
          })
        );

        // 保存到缓存（IndexedDB，避免大体量任务撑爆 localStorage）
        await userTasksSetItem(CACHE_KEY, {
          items: allTasks,
          targetUser: targetUserSummary,
        });
        await userTasksSetItem(TIME_KEY, Date.now().toString());
        lastUpdateTime.value = formatTime(Date.now());
      } else if (requestedAdminQueryValue) {
        adminTargetUserSummary.value = null;
        adminTargetUserNotFound.value = false;
      }
    }

    if (allTasks !== null) {
      // 更新页面显示的任务列表
      tasks.value = allTasks;

      // 重新应用当前筛选条件（保持已选的 tab 过滤状态）
      await filterTasksAsync();
      // 按工具类型分组并更新 Vuex Store
      const tasksByTool = allTasks.reduce((acc, task) => {
        const key = historyCacheKeyForTask(task);
        if (!key) return acc;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(task);
        return acc;
      }, {});

      // 为每个工具类型更新缓存
      Object.keys(tasksByTool).forEach((cacheKey) => {
        store.commit("ai/SET_HISTORY_CACHE", {
          toolName: cacheKey,
          tasks: tasksByTool[cacheKey],
        });
      });

      // // 获取每个工具的 页面配置
      // const pageConfigs = await Promise.all(
      //   Object.keys(tasksByTool).map(async (toolName) => {
      //     if (!toolName) return;
      //     const toolPath = "/" + toolPathKey(toolName);
      //     const pageConfig = await getToolPageConfig(toolPath);
      //     console.log(toolPath, pageConfig);
      //     return { toolName, pageConfig };
      //   })
      // );
      taskConfigData.value = await getTaskConfig();
      console.log("所有页面配置:", taskConfigData.value);

      console.log("已更新历史记录缓存，工具类型:", Object.keys(tasksByTool));
    }
  } catch (error) {
    console.error("Failed to load tasks:", error);
    toast.add({
      title: "Failed to load tasks",
      color: "error",
      icon: "i-heroicons-exclamation-triangle",
    });
  } finally {
    loading.value = false;
  }
}

/** 将提示复制到剪贴板 */
async function copyPrompt(text) {
  try {
    // 现代方法 是否归档
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      // 传统方法
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }

    toast.add({
      title: "Cue word copied",
      description: text,
      color: "neutral",
      icon: "i-lucide-copy",
      ui: {
        wrapper: "w-0 flex-1 flex flex-col max-h-48 overflow-y-auto",
      },
    });
  } catch (err) {
    console.error("复制失败:", err);
    toast.add({
      title: "Reproduction Failure",
      description: "Please copy the text manually",
      color: "error",
      icon: "i-lucide-alert-circle",
    });
  }
}

/** 处理删除任务 */
async function handleDeleteTask(taskId) {
  deletingTaskId.value = taskId;
  try {
    // 找到要删除的任务
    const taskToDelete = tasks.value.find((task) => task.id === taskId);

    await store.dispatch("ai/setUserHistory", {
      id: taskId,
      set_type: "delete",
    });

    // 从页面任务列表中删除
    tasks.value = tasks.value.filter((task) => task.id !== taskId);
    deletePopoverOpen.value[taskId] = false;

    // 如果任务存在，同时更新 store 中对应工具的缓存
    const delKey = historyCacheKeyForTask(taskToDelete);
    if (taskToDelete && delKey) {
      const cachedHistory = store.getters["ai/getHistoryByTool"](delKey);
      if (cachedHistory && Array.isArray(cachedHistory)) {
        const updatedHistory = cachedHistory.filter(
          (task) => task.id !== taskId
        );
        store.commit("ai/SET_HISTORY_CACHE", {
          toolName: delKey,
          tasks: updatedHistory,
        });
      }
    }

    toast.add({
      title: "Task deleted successfully",
      color: "success",
      icon: "i-heroicons-check-circle",
    });
  } catch (error) {
    console.error("Failed to delete task:", error);
    toast.add({
      title: "Failed to delete task",
      color: "error",
      icon: "i-heroicons-exclamation-triangle",
    });
  } finally {
    deletingTaskId.value = null;
    // 重新应用当前筛选条件（保持已选的 tab 过滤状态）
    await filterTasksAsync();
  }
}

/** 下载图片 */
async function downloadImage(task) {
  const urls = getTaskOutputUrls(task);
  if (!urls.length) return;

  try {
    // 如果是多图片，传递所有URL；否则只传递单个URL
    const imageurls = urls.length > 1 ? urls.join(",") : urls[0];
    const filename = urls[0].split("/").pop();
    const toolClassName = toolTitle(task.toolName, task).label;
    const params = new URLSearchParams({
      img_path: imageurls,
      // prev_route1 = 用户实际所在页(此前硬编码 /free-tools/image/<slug>,对 AI 任务是 404 链接)。
      prev_route1: typeof window !== "undefined" ? window.location.pathname : "",
      prev_route1_title: toolClassName,
      img_title: filename,
    });
    window.open(`/download/?${params.toString()}`);
  } catch (error) {
    console.error("Failed to download image:", error);
    toast.add({
      title: "Failed to download image",
      color: "error",
      icon: "i-heroicons-exclamation-triangle",
    });
  }
}

/** 复制任务 */
async function copyTask(message) {
  const text = typeof message === "string" ? message : JSON.stringify(message);
  try {
    // 现代方法 是否归档
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      // 传统方法
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }

    toast.add({
      title: "Copied",
      description: text,
      color: "success",
      icon: "i-lucide-copy",
      ui: {
        wrapper: "w-0 flex-1 flex flex-col max-h-48 overflow-y-auto",
      },
    });
  } catch (err) {
    console.error("Copy failed:", err);
    toast.add({
      title: "Copy failed",
      description: "Please copy the text manually",
      color: "error",
      icon: "i-lucide-alert-circle",
    });
  }
}

/** 生成视频 */
function generateVideo(task) {
  console.log("Generate video for task:", task);
  // TODO: 实现生成视频功能
  const selectedOutputUrl = getTaskSelectedOutputUrl(task);
  const newTask = {
    ...task,
    uploadFieldMap: {
      input_image: stripR2UploadValue(selectedOutputUrl || task.outPutImageUrl),
    },
    prompt: "",
    toolName: "image-to-video",
    isFirstModel: true,
  };
  console.log("Re-edit task:", newTask);
  // 将任务数据和操作类型存储到 store
  store.commit("ai/SET_PENDING_TASK", {
    task: newTask,
    action: "re-edit",
  });
  navigateTo(`/ai/video/image-to-video/`);
}

/** 设置用户积分 */
const setUserCreditsSum = (credits) =>
  store.dispatch("user/setUserCreditsSum", credits);

/** 是否是开发环境 */
const isDev = computed(() => store.state.user.is_dev);

/** 获取任务状态的通用函数 */
async function getTaskStatus(taskIds) {
  try {
    const res = await store.dispatch("ai/getTaskStatus", {
      ids: taskIds.join(","),
      userId: userId.value,
    });
    if (res && res.data && res.status === 200) {
      const taskDatas = res.data.taskDatas;
      const credits_sum = res.data.credits_sum;
      await setUserCreditsSum(credits_sum);
      return taskDatas;
    }
  } catch (error) {
    console.error("获取任务状态失败:", error);
    throw new Error(error);
  }
  return null;
}

/** 刷新单个任务 */
async function refreshSingleTask(taskId) {
  refreshingTaskId.value = taskId;
  try {
    const task = await getTaskStatus([taskId]);
    // 更新任务状态
    await updateTaskStatus(task);
  } catch (error) {
    console.error("刷新任务失败:", error);
    toast.add({
      title: "Failed to refresh the mission",
      color: "error",
      icon: "i-heroicons-exclamation-triangle",
    });
  } finally {
    refreshingTaskId.value = null;
  }
}

/** 刷新所有预测中的任务 */
async function refreshAllPendingTasks() {
  const pendingTasks = tasks.value.filter((task) => isTaskPending(task));
  if (pendingTasks.length === 0) return;

  isRefreshingAll.value = true;
  try {
    const taskIds = pendingTasks.map((task) => task.id);
    const tasksList = await getTaskStatus(
      Array.isArray(taskIds) ? taskIds : [taskIds]
    );

    // 批量更新任务状态
    for (const task of tasksList) {
      await updateTaskStatus(task);
    }
  } catch (error) {
    console.error("刷新全部任务失败:", error);
    toast.add({
      title: "Failed to refresh all tasks",
      color: "error",
      icon: "i-heroicons-exclamation-triangle",
    });
  } finally {
    isRefreshingAll.value = false;
  }
}

/** 更新任务状态的通用函数 */
async function updateTaskStatus(taskData) {
  const taskId = taskData.id;
  const status = taskData.status;
  const outputUrls = resolveUserUploadValue(taskData.output);
  const outPutImageUrl = outputUrls[0] || null;
  const completed_at = taskData.completed_at;

  // 如果任务失败，从列表中删除
  if (status === "failed") {
    tasks.value = tasks.value.filter((task) => task.id !== taskId);
    const errorMessage = taskData.output;
    // 同时更新所有工具类型的缓存
    const allKeys = [
      ...new Set(tasks.value.map((t) => historyCacheKeyForTask(t))),
    ].filter(Boolean);
    allKeys.forEach((cacheKey) => {
      const cachedHistory = store.getters["ai/getHistoryByTool"](cacheKey);
      if (cachedHistory && Array.isArray(cachedHistory)) {
        const updatedHistory = cachedHistory.filter(
          (task) => task.id !== taskId
        );
        store.commit("ai/SET_HISTORY_CACHE", {
          toolName: cacheKey,
          tasks: updatedHistory,
        });
      }
    });

    toast.add({
      title: "Failed",
      description: errorMessage,
      color: "error",
      icon: "i-heroicons-x-circle",
      duration: 5000,
    });
    return;
  }

  // 更新任务状态和数据
  const taskIndex = tasks.value.findIndex((task) => task.id === taskId);
  if (taskIndex !== -1) {
    const updatedTask = {
      ...tasks.value[taskIndex],
      status: status,
      outPutImageUrl: outPutImageUrl || tasks.value[taskIndex].outPutImageUrl,
      outputUrls: outputUrls.length
        ? outputUrls
        : tasks.value[taskIndex].outputUrls,
      createdAt: new Date(
        /\d{2}:\d{2}:\d{2}$/.test(completed_at)
          ? completed_at + "Z"
          : completed_at
      ).getTime(),
    };
    tasks.value[taskIndex] = updatedTask;

    // 如果任务成功完成，显示提示
    if (status === "succeeded") {
      toast.add({
        title: "Task completed",
        color: "success",
        icon: "i-heroicons-check-circle",
      });
    }

    const cacheKey = historyCacheKeyForTask(updatedTask);
    if (cacheKey) {
      const cachedHistory = store.getters["ai/getHistoryByTool"](cacheKey);
      if (cachedHistory && Array.isArray(cachedHistory)) {
        const updatedHistory = cachedHistory.map((task) => {
          if (task.id === taskId) {
            return {
              ...task,
              status: status,
              outPutImageUrl: outPutImageUrl || task.outPutImageUrl,
              outputUrls: outputUrls.length ? outputUrls : task.outputUrls,
              createdAt: completed_at
                ? new Date(completed_at).getTime()
                : task.createdAt,
            };
          }
          return task;
        });
        store.commit("ai/SET_HISTORY_CACHE", {
          toolName: cacheKey,
          tasks: updatedHistory,
        });
      }
    }
  }
}

const getUploadFieldMapFromTask = (task, modelKey) => {
  if (task.uploadFieldMap) {
    const cloned = JSON.parse(JSON.stringify(task.uploadFieldMap));
    Object.keys(cloned).forEach((key) => {
      cloned[key] = stripR2UploadValue(cloned[key]);
    });
    return cloned;
  }

  const fields = getModelFields(modelKey);
  const map = {};

  if (task.inPutImages) {
    Object.entries(task.inPutImages).forEach(([key, value]) => {
      if (fields?.[key]?.ui?.component === "uploader") {
        map[key] = stripR2UploadValue(value?.urls || []);
      }
    });
  }

  if (Array.isArray(task.videoFiles) && task.videoFiles.length > 0) {
    const videoField = getUploaderFieldEntries(fields).find(([, def]) => {
      return detectMediaType(def) === "video";
    });
    if (videoField) {
      const [fieldKey] = videoField;
      map[fieldKey] = stripR2UploadValue(task.videoFiles);
    }
  }

  if (Array.isArray(task.audioFiles) && task.audioFiles.length > 0) {
    const audioField = getUploaderFieldEntries(fields).find(([, def]) => {
      return detectMediaType(def) === "audio";
    });
    if (audioField) {
      const [fieldKey] = audioField;
      map[fieldKey] = stripR2UploadValue(task.audioFiles);
    }
  }

  return map;
};

/** 为更多菜单获取下拉项目 */
function getDropdownItems(task) {
  const configKey = getTaskConfigKey(task);
  if (!taskConfigData.value?.[configKey]) {
    return [];
  }
  const canOperateCurrentModel = isTaskModelEnabled(task);
  debugger;
  let dropdownItems = [];

  if (canOperateCurrentModel) {
    dropdownItems.push({
      label: aiToolConfig[task.toolName]?.regenerateBtnLabel || "Regenerate",
      icon: "material-symbols-light:star-shine-outline-rounded",
      disabled: isTaskPending(task),
      onSelect: () => {
        console.log("Regenerate task:", task);
        store.commit("ai/SET_PENDING_TASK", {
          task: task,
          action: "regenerate",
        });
        navigateTo(getTaskNavigatePath(task));
      },
    });
  }

  /** Refine */
  if (taskConfigData.value[configKey]?.isShowRefineImage) {
    dropdownItems.push({
      label: "Refine",
      icon: "material-symbols-light:photo-library-rounded",
      onSelect: () => {
        const selectedOutputUrl = getTaskSelectedOutputUrl(task);
        const newTask = {
          ...task,
          uploadFieldMap: {
            input_image: stripR2UploadValue(selectedOutputUrl || task.outPutImageUrl),
          },
          prompt: "",
          toolName: "image-to-image",
          isFirstModel: true,
        };
        console.log("Re-edit task:", newTask);
        // 将任务数据和操作类型存储到 store
        store.commit("ai/SET_PENDING_TASK", {
          task: newTask,
          action: "re-edit",
        });
        navigateTo(`/ai/image/image-to-image/`);
      },
    });
  }

  /** ReEdit */
  if (
    taskConfigData.value[configKey]?.isShowReEditImage &&
    canOperateCurrentModel
  ) {
    dropdownItems.push({
      label: "Re-edit",
      icon: "material-symbols-light:edit-road-rounded",
      onSelect: () => {
        const uploadFieldMap = getUploadFieldMapFromTask(task, task.model);
        const newTask = {
          ...task,
          uploadFieldMap: uploadFieldMap,
          toolName: task.toolName,
        };

        // 将任务数据和操作类型存储到 store
        store.commit("ai/SET_PENDING_TASK", {
          task: newTask,
          action: "re-edit",
        });

        navigateTo(getTaskNavigatePath(task));
      },
    });
  }
  dropdownItems.push({
    label: "Copy Task Info",
    icon: "material-symbols-light:content-copy-outline-rounded",
    onSelect: () => copyTask(task.id),
  });

  return dropdownItems;
}

/** 清理定时器的通用函数 */
const clearRefreshTimer = () => {
  if (refreshTimer.value) {
    console.log("清理定时器");
    clearTimeout(refreshTimer.value);
    refreshTimer.value = null;
  }
};

/** 清理筛选定时器 */
const clearFilterTimer = () => {
  if (filterTimeout.value) {
    clearTimeout(filterTimeout.value);
    filterTimeout.value = null;
  }
};

/** 定时器引用 */
const refreshTimer = ref(null);

/** 异步递归定时器：等待刷新完成后再等待2秒 */
const startAutoRefresh = async () => {
  // 检查是否还有预测中的任务
  if (!hasPendingTasks.value) {
    console.log("没有预测中的任务，停止自动刷新");
    refreshTimer.value = null;
    return;
  }

  console.log("自动刷新预测中的任务", new Date().toISOString());

  try {
    // 等待刷新完成
    await refreshAllPendingTasks();
  } catch (error) {
    console.error("自动刷新出错:", error);
  }

  // 刷新完成后，等待4秒再进行下一次刷新
  if (hasPendingTasks.value) {
    refreshTimer.value = setTimeout(() => {
      startAutoRefresh();
    }, 40000);
  } else {
    console.log("所有任务已完成，停止自动刷新");
    refreshTimer.value = null;
  }
};

/** 监听预测中的任务，自动刷新状态 */
watch(
  hasPendingTasks,
  (hasPending) => {
    if (hasPending) {
      // 如果有预测中的任务且定时器未启动，启动异步递归定时器
      if (!refreshTimer.value) {
        console.log("启动定时器：每4秒自动刷新预测中的任务");
        startAutoRefresh();
      }
    } else {
      // 如果没有预测中的任务，清除定时器
      clearRefreshTimer();
    }
  },
  { immediate: true }
);

/** 组件激活时检查是否需要启动定时器（从 keep-alive 缓存中恢复时） */
onActivated(() => {
  console.log("组件激活时检查定时器 (onActivated)");
  // 如果有预测中的任务且定时器未运行，重新启动定时器
  if (hasPendingTasks.value && !refreshTimer.value) {
    console.log("检测到预测中的任务，重新启动定时器");
    startAutoRefresh();
  }
});

/** 组件失活时清理定时器（keep-alive 缓存时） */
onDeactivated(() => {
  console.log("组件失活时清理定时器 (onDeactivated)");
  clearRefreshTimer();
  clearFilterTimer();
});

/** 组件卸载时清理定时器 */
onUnmounted(() => {
  console.log("组件卸载时清理定时器 (onUnmounted)");
  clearRefreshTimer();
  clearFilterTimer();
});
</script>

<style scoped>
.my-tasks-page {
  min-height: 92vh;
}

.cards-scroll {
  max-height: calc(100vh - 200px);
  overflow: auto;
}

/* Tab切换淡入淡出动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* 任务项动画 */
.task-item-enter-active {
  transition: all 0.3s ease;
}

.task-item-leave-active {
  transition: all 0.2s ease;
}

.task-item-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.task-item-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.task-item-move {
  transition: transform 0.3s ease;
}
</style>
